import axios from "axios";
import config from "./config";
import { getHeaders } from "./apiHelpers";

const BASE_URL = config.BASE_URL;

/** Structured-field request shape — backend signals which inline
 *  input the widget should render below the latest assistant bubble. */
export interface AnonChatFieldRequest {
  name: string;
  label: string;
  /** "signin" renders an inline sign-in form (email + password +
   *  forgot-password link) — used when the captured email matches
   *  an existing member. The chat continues in the same widget
   *  after auth. */
  type: "email" | "text" | "phone" | "date" | "select" | "yesno" | "signin";
  required?: boolean;
  options?: string[];
  placeholder?: string;
  /** For type='signin': prefill the email field. */
  email_prefill?: string;
}

export interface AnonAiChatResponse {
  success: boolean;
  data?: {
    response: string;
    session_id: string;
    lead_id: string | null;
    should_redirect_to_signin: boolean;
    should_close?: boolean;
    conversation_id: string | null;
    pending_field?: AnonChatFieldRequest | null;
    field_error?: { name: string; error: string };
  };
  message?: string;
  enabled?: boolean;
}

/**
 * Send a message to the anonymous AI customer-service endpoint.
 * No auth required. Tenant must have settings.ai_chat_anonymous_enabled=true
 * or this returns 503 with { enabled: false }.
 */
export interface AnonAiChatRequest {
  message?: string;
  sessionId: string | null;
  leadId: string | null;
  /** Structured field response — when the visitor fills the inline
   *  input rendered for a previous pending_field. Backend validates +
   *  applies deterministically (no LLM parsing). */
  fieldResponse?: { name: string; value: string | number };
  /** Page context captured at the call site so the LLM can
   *  disambiguate visitor intent (e.g. "I want to cancel" on the
   *  events page vs the membership page). Optional. */
  pageContext?: {
    page_path: string;
    page_url: string;
    page_title: string;
  };
}

export async function sendAnonAiChatMessage(
  reqOrMessage: string | AnonAiChatRequest,
  sessionId?: string | null,
  leadId?: string | null
): Promise<AnonAiChatResponse> {
  // Backwards-compat: accept either a plain string + sessionId/leadId
  // (legacy call sites) or a structured AnonAiChatRequest object.
  const args: AnonAiChatRequest =
    typeof reqOrMessage === "string"
      ? { message: reqOrMessage, sessionId: sessionId ?? null, leadId: leadId ?? null }
      : reqOrMessage;

  // Auto-capture page context if the caller didn't pass one. Lets
  // every existing call site benefit without per-site changes.
  const pageContext =
    args.pageContext ||
    (typeof window !== "undefined"
      ? {
          page_path: (() => {
            const segs = (window.location.pathname || "")
              .split("/")
              .filter(Boolean);
            return segs.length > 1 ? `/${segs.slice(1).join("/")}` : "/";
          })(),
          page_url: window.location.href || "",
          page_title: typeof document !== "undefined" ? document.title || "" : "",
        }
      : undefined);

  try {
    // Send auth header when an authToken is present in localStorage.
    // The backend's optionalAuth middleware will decode it and elevate
    // userContext to authenticated member — only matters once the
    // visitor has signed in mid-chat via the inline signin field.
    // Anonymous traffic continues to work without it.
    const hasToken =
      typeof window !== "undefined" && !!localStorage.getItem("authToken");
    const resp = await axios.post(
      `${BASE_URL}/public/ai-chat`,
      {
        message: args.message,
        session_id: args.sessionId || undefined,
        lead_id: args.leadId || undefined,
        field_response: args.fieldResponse || undefined,
        page_context: pageContext,
      },
      { headers: getHeaders(hasToken) }
    );
    return resp.data;
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 503) {
      return { success: false, enabled: false, message: "AI chat is not enabled for this site." };
    }
    // 400 with field_error is a validation failure — pass through so
    // the widget can render an inline error below the input.
    if (status === 400 && err?.response?.data?.data?.field_error) {
      return {
        success: false,
        message: err.response.data.message,
        data: err.response.data.data,
      };
    }
    return {
      success: false,
      message: err?.response?.data?.message || err?.message || "Failed to send message",
    };
  }
}
